import { useQuery } from "@tanstack/react-query";

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { apiRequest } from "shared/api/apiRequest";
import { getSocietyDashboardIdContract } from "shared/api/contracts/society/societyId/dashboards/dashboardId";
import Spinner from "shared/components/common/Spinner";
import { useSocietySession } from "shared/context/SocietySessionContext";
import { useSocietyColorPalette } from "shared/hooks/useSocietyColorPalette";

const PieChartModuleType = ({ dashboardId }: { dashboardId: number }) => {
  const { societyId } = useSocietySession();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboardModule", dashboardId],
    queryFn: () =>
      apiRequest({
        contract: getSocietyDashboardIdContract,
        inputs: {
          params: { societyId, dashboardId },
        },
      }),
    refetchOnWindowFocus: false,
  });

  const { paletteList } = useSocietyColorPalette(true);

  if (
    (isLoading && !data) ||
    !data ||
    !data.data ||
    !Array.isArray(data.data)
  ) {
    return (
      <div className="col-span-6 flex h-50 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const { dashboardModule, data: results } = data;

  const { innerRadius, outerRadius } = dashboardModule.componentConfig ?? {};

  return (
    <div className="flex flex-col">
      <span className="mb-2 block min-h-16 text-center text-base font-semibold leading-tight">
        {dashboardModule.title}
      </span>
      <div className="mx-auto w-35 max-w-full">
        <ResponsiveContainer width="100%" aspect={1}>
          <PieChart>
            <Pie
              data={results}
              dataKey="value"
              nameKey="name"
              minAngle={3}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius ? `${innerRadius}%` : "55%"}
              outerRadius={outerRadius ? `${outerRadius}%` : "80%"}
            >
              {results.map((entry, index) => {
                const fill =
                  dashboardModule.setsConfig?.find(
                    (f) => f.dataKey === entry.name,
                  )?.fill ?? paletteList[index % paletteList.length];
                return <Cell key={`cell-${index}`} fill={fill} />;
              })}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PieChartModuleType;
